// EmailVerificationService.js

/**
 * EmailVerificationService.js
 *
 * Client helpers for Firebase Auth email verification.
 */

import { reload, sendEmailVerification } from 'firebase/auth';

import { auth, APP_BASE_URL } from 'firebaseConfig';

const RESEND_COOLDOWN_MS = 60_000;

function normalizeAuthUser(authUser) {
  return authUser || auth.currentUser || null;
}

/**
 * Sends a verification email to the signed-in user.
 *
 * @param {import('firebase/auth').User} [authUser]
 * @returns {Promise<void>}
 */
export async function sendAccountVerificationEmail(authUser = auth.currentUser) {
  const user = normalizeAuthUser(authUser);

  if (!user) {
    throw new Error('You must be signed in to send a verification email.');
  }

  if (user.emailVerified) {
    return;
  }

  await sendEmailVerification(user, {
    handleCodeInApp: false,
    url: APP_BASE_URL,
  });
}

/**
 * Reloads the auth user and refreshes the ID token.
 *
 * @param {import('firebase/auth').User} [authUser]
 * @returns {Promise<boolean>} Whether the email is verified after reload.
 */
export async function reloadAndCheckEmailVerified(authUser = auth.currentUser) {
  const user = normalizeAuthUser(authUser);

  if (!user) {
    return false;
  }

  await reload(user);
  await user.getIdToken(true);

  return user.emailVerified === true;
}

export function maskEmailAddress(email) {
  const normalized = String(email || '').trim().toLowerCase();
  if (!normalized || !normalized.includes('@')) {
    return normalized || 'your email';
  }

  const [localPart, domain] = normalized.split('@');
  if (!localPart || !domain) {
    return normalized;
  }

  const visible = localPart.length <= 2
    ? `${localPart[0] || '*'}*`
    : `${localPart.slice(0, 2)}***`;

  return `${visible}@${domain}`;
}

export const EMAIL_VERIFICATION_RESEND_COOLDOWN_MS = RESEND_COOLDOWN_MS;
